import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import {
  Wand2, Sparkles, CalendarDays, Loader2, ArrowRight,
  Linkedin, Instagram, Facebook, Twitter, Youtube, AlertCircle, FileText, Save,
  Layers, ScanText, Send, Rocket,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useClients } from '../hooks/useClients';
import { toast } from 'sonner';
import { CaptionLengthPicker } from '../components/CaptionLengthPicker';
import { AdvancedPromptDetails } from '../components/AdvancedPromptDetails';
import { StyleProfileSelector } from '../components/StyleProfileSelector';
import { FunnelCtaPicker, buildTrackedUrl } from '../components/FunnelCtaPicker';
import { LaunchCampaignDialog } from '../components/LaunchCampaignDialog';

const PLATFORM_OPTIONS = [
  { id: 'linkedin',  label: 'LinkedIn',     Icon: Linkedin },
  { id: 'instagram', label: 'Instagram',    Icon: Instagram },
  { id: 'facebook',  label: 'Facebook',     Icon: Facebook },
  { id: 'bluesky', label: 'Bluesky',  Icon: Twitter },
  { id: 'youtube',   label: 'YouTube',      Icon: Youtube },
];

const TONE_OPTIONS = [
  'Professional', 'Casual', 'Inspirational', 'Humorous', 'Bold', 'Educational',
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const inDaysISO = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export const AIPostManagerPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [prompt, setPrompt] = useState('');
  const [audience, setAudience] = useState('');
  const [tone, setTone] = useState('Professional');
  const [platforms, setPlatforms] = useState(['linkedin']);
  const [postsPerDay, setPostsPerDay] = useState(2);
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(inDaysISO(6));
  // Anti-spam scheduling — global min gap + daily window. Defaults match backend.
  const [spacingHours, setSpacingHours] = useState(4);
  const [dailyStartHour, setDailyStartHour] = useState(6);
  // captionLength state mirrors the new picker shape: { mode, words? }
  const [captionLength, setCaptionLength] = useState({ mode: 'auto' });
  const [styleExample, setStyleExample] = useState('');
  const [extraInstructions, setExtraInstructions] = useState('');
  const [ctaFunnel, setCtaFunnel] = useState(null);
  const [goal, setGoal] = useState('none');
  const [blueprint, setBlueprint] = useState(null);
  const [buildingStrategy, setBuildingStrategy] = useState(false);
  const [draftingEmails, setDraftingEmails] = useState(false);
  const [emailExt, setEmailExt] = useState(null);
  const [emailLists, setEmailLists] = useState([]);
  const [autoEnrollList, setAutoEnrollList] = useState('none');
  const [launchOpen, setLaunchOpen] = useState(false);

  useEffect(() => {
    api.get('/email/lists').then((r) => setEmailLists(r.data?.lists || [])).catch(() => setEmailLists([]));
  }, []); // eslint-disable-line
  // Bump this on auto-apply so the AdvancedPromptDetails accordion remounts
  // with defaultOpen=true and the user immediately sees the populated values.
  const [advancedRevealKey, setAdvancedRevealKey] = useState(0);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewMeta, setPreviewMeta] = useState(null); // { spilled_days, last_scheduled_for }
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clientId, setClientId] = useState('none');
  const [viewMode, setViewMode] = useState('detailed'); // 'detailed' | 'scan'
  const { clients } = useClients();
  // Track the last client we auto-applied so we don't repeatedly re-apply on
  // re-renders, and so we only run when the user actively changes clients
  // (not when the page first mounts with 'none' selected).
  const lastAutoAppliedClientRef = useRef(null);

  // When the user picks a client that has a default style profile, auto-load
  // it into the form. The user can still pick a different profile or clear it
  // from the StyleProfileSelector right below — this is just a head start.
  useEffect(() => {
    if (clientId === 'none' || clientId === lastAutoAppliedClientRef.current) return;
    const client = clients.find((c) => c.id === clientId);
    if (!client || !client.default_style_profile_id) {
      lastAutoAppliedClientRef.current = clientId;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/style-profiles');
        if (cancelled) return;
        const profile = (r.data?.profiles || []).find(
          (p) => p.id === client.default_style_profile_id
        );
        if (!profile) {
          lastAutoAppliedClientRef.current = clientId;
          return;
        }
        setStyleExample(profile.style_example || '');
        setExtraInstructions(profile.extra_instructions || '');
        setCaptionLength(
          profile.caption_length_mode === 'fixed' && profile.caption_length_words
            ? { mode: 'fixed', words: profile.caption_length_words }
            : profile.caption_length_mode === 'per_platform'
              ? { mode: 'per_platform' }
              : { mode: 'auto' }
        );
        // Force the Advanced section open so the user can SEE the populated
        // style_example / extra_instructions without an extra click.
        if (profile.style_example || profile.extra_instructions) {
          setAdvancedRevealKey((k) => k + 1);
        }
        toast.success(`Auto-applied: ${profile.name}`);
        lastAutoAppliedClientRef.current = clientId;
      } catch (_) {
        // silent — selector below will still let the user pick manually
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, clients]);

  const togglePlatform = (id) => {
    setPlatforms((cur) =>
      cur.includes(id) ? cur.filter((p) => p !== id) : [...cur, id]
    );
  };

  const dayCount = (() => {
    if (!startDate || !endDate) return 0;
    const a = new Date(startDate), b = new Date(endDate);
    return Math.max(0, Math.round((b - a) / 86400000) + 1);
  })();

  const totalEstimate = postsPerDay * platforms.length * dayCount;

  // ---- Quick scan helpers ----
  // Group preview items by ISO week (Mon-anchored) so reviewing 30+ days of
  // drafts becomes a 4-section accordion the user can collapse / expand.
  const groupByWeek = (items) => {
    const buckets = new Map();
    items.forEach((it) => {
      const d = new Date(it.scheduled_for);
      const day = d.getUTCDay() || 7; // Mon=1..Sun=7
      const monday = new Date(d);
      monday.setUTCDate(d.getUTCDate() - day + 1);
      monday.setUTCHours(0, 0, 0, 0);
      const key = monday.toISOString().slice(0, 10);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(it);
    });
    return Array.from(buckets.entries())
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([weekStart, weekItems]) => {
        const start = new Date(`${weekStart}T00:00:00Z`);
        const end = new Date(start);
        end.setUTCDate(start.getUTCDate() + 6);
        const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return {
          key: weekStart,
          label: `${fmt(start)} – ${fmt(end)}`,
          count: weekItems.length,
          items: weekItems.sort(
            (a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for)
          ),
        };
      });
  };
  const previewWeeks = groupByWeek(previewItems);

  const buildPayload = () => ({
    prompt: prompt.trim(),
    platforms,
    start_date: startDate,
    end_date: endDate,
    posts_per_day: postsPerDay,
    tone,
    audience: audience.trim() || null,
    client_id: clientId !== 'none' ? clientId : null,
    min_spacing_hours: spacingHours,
    daily_start_hour: dailyStartHour,
    daily_end_hour: 22,
    target_words: captionLength.mode === 'fixed' ? captionLength.words : null,
    per_platform_lengths: captionLength.mode === 'per_platform',
    style_example: styleExample?.trim() || null,
    extra_instructions: extraInstructions?.trim() || null,
    cta_url: ctaFunnel ? buildTrackedUrl(ctaFunnel, 'campaign') : null,
    cta_label: ctaFunnel ? ctaFunnel.title : null,
    goal: goal !== 'none' ? goal : null,
    blueprint: blueprint || null,
  });

  const GOALS = [
    { v: 'authority', l: 'Build brand authority' },
    { v: 'engagement', l: 'Increase engagement' },
    { v: 'awareness', l: 'Expand brand awareness' },
    { v: 'leads', l: 'Generate qualified leads' },
    { v: 'traffic', l: 'Drive website traffic' },
    { v: 'launch', l: 'Product / service launch' },
    { v: 'nurture', l: 'Nurture customers' },
  ];

  const buildStrategy = async () => {
    if (prompt.trim().length < 10) { toast.error('Tell me a bit more about the business (10+ chars)'); return; }
    if (platforms.length === 0) { toast.error('Select at least one platform'); return; }
    setBuildingStrategy(true);
    try {
      const r = await api.post('/campaigns/blueprint', {
        prompt: prompt.trim(), goal, platforms, audience: audience.trim() || null, tone,
      });
      setBlueprint(r.data.blueprint);
      toast.success('Strategy ready — review or tweak it, then generate');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not build strategy');
    } finally { setBuildingStrategy(false); }
  };

  const updateBp = (field, value) => setBlueprint((b) => ({ ...b, [field]: value }));

  const draftEmails = async () => {
    if (!blueprint) return;
    setDraftingEmails(true);
    try {
      const r = await api.post('/campaigns/email-extension', {
        prompt: prompt.trim(), goal, audience: audience.trim() || null, tone,
        blueprint,
        cta_url: ctaFunnel ? buildTrackedUrl(ctaFunnel, 'email') : null,
        list_id: autoEnrollList !== 'none' ? autoEnrollList : null,
      });
      setEmailExt(r.data);
      toast.success('Matching emails drafted into Email Marketing');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not draft emails');
    } finally { setDraftingEmails(false); }
  };

  const handlePreview = async () => {
    if (prompt.trim().length < 10) {
      toast.error('Tell me a bit more about the campaign (10+ chars)');
      return;
    }
    if (platforms.length === 0) {
      toast.error('Select at least one platform');
      return;
    }
    if (totalEstimate === 0) {
      toast.error('Pick a valid date range');
      return;
    }
    setPreviewing(true);
    setPreviewItems([]);
    setPreviewMeta(null);
    try {
      // Use the async background-job pattern to bypass the proxy's ~60s
      // timeout. For long campaigns (e.g. 30 days × 5 platforms × 3/day)
      // the synchronous /preview endpoint can hit 504; this route returns
      // a job_id immediately and we poll until complete.
      const payload = buildPayload();
      const startResp = await api.post('/campaigns/preview-async', payload);
      const jobId = startResp.data.job_id;

      // Poll every 2s for up to 5 minutes. Each poll is a cheap DB lookup
      // so the overhead is negligible. Show user-friendly progress text.
      const start = Date.now();
      const TIMEOUT_MS = 5 * 60 * 1000;
      let finalResult = null;
      while (Date.now() - start < TIMEOUT_MS) {
        await new Promise((r) => setTimeout(r, 2000));
        const status = await api.get(`/campaigns/preview-status/${jobId}`);
        if (status.data.status === 'complete') {
          finalResult = status.data.result;
          break;
        }
        if (status.data.status === 'failed') {
          throw new Error(status.data.error || 'Preview generation failed');
        }
      }
      if (!finalResult) {
        throw new Error('Preview timed out — try a smaller date range or fewer platforms');
      }
      setPreviewItems(finalResult.items || []);
      setPreviewMeta({
        spilled_days: finalResult.spilled_days || 0,
        last_scheduled_for: finalResult.last_scheduled_for || null,
      });
      if (finalResult.capped) {
        toast.message('Capped to 5 posts/day/platform');
      } else if (finalResult.spilled_days > 0) {
        toast.success(
          `${finalResult.total} drafts ready — campaign extends ${finalResult.spilled_days} day(s) past your end date due to ${spacingHours}h spacing`
        );
      } else {
        toast.success(`${finalResult.total} draft posts ready to review`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSaveAsDrafts = async () => {
    if (previewItems.length === 0) {
      toast.error('Generate a preview first');
      return;
    }
    if (!window.confirm(`Save ${previewItems.length} draft post(s)? You can edit, schedule, or delete them later.`)) return;
    setSaving(true);
    try {
      // Use /save-from-preview instead of /generate — persists the items we
      // already have in memory from /preview-async, skipping the LLM entirely.
      // Avoids the 504 timeout that hit /campaigns/generate for large plans
      // (since /generate re-ran the LLM for every save).
      const base = buildPayload();
      const r = await api.post('/campaigns/save-from-preview', {
        ...base,
        items: previewItems,
        goal: goal !== 'none' ? goal : null,
        blueprint: blueprint || null,
      });
      toast.success(`Created ${r.data.total} drafts`);
      navigate(`/posts?campaign=${r.data.campaign_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save drafts');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="ai-post-manager-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <Wand2 className="w-6 h-6 text-indigo-600" />
          AI Post Manager
        </h1>
        <p className="text-zinc-500 text-sm mt-0.5">
          Plan an entire campaign in seconds — AI drafts platform-tuned posts, hashtags, image prompts, and a smart schedule. Always saved as drafts for your review.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT — Brief */}
        <Card className="border-zinc-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              Campaign brief
            </CardTitle>
            <CardDescription>The AI uses this to draft every post.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="prompt">What is this campaign about?</Label>
                <span
                  className={`text-xs tabular-nums ${prompt.length > 2000 ? 'text-rose-600 font-medium' : prompt.length > 1800 ? 'text-amber-600' : 'text-zinc-400'}`}
                  data-testid="campaign-prompt-char-count"
                >
                  {prompt.length.toLocaleString()} / 2,000
                </span>
              </div>
              <Textarea
                id="prompt"
                placeholder="e.g. Promote our new AI marketing platform launch — emphasize time savings, multi-platform scheduling, and the 5-day free trial."
                rows={5}
                maxLength={2000}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                data-testid="campaign-prompt-input"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="audience">Audience (optional)</Label>
                <Input
                  id="audience"
                  placeholder="e.g. Small business owners"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  data-testid="campaign-audience-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="campaign-tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TONE_OPTIONS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {clients.length > 0 && (
              <div className="space-y-2">
                <Label>Tag for client (optional)</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger data-testid="campaign-client-select">
                    <SelectValue placeholder="Don't tag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Don't tag</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full"
                            style={{ background: c.brand_color || '#6366f1' }}
                          />
                          {c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-zinc-500">
                  Stamps every generated draft with this client so they're easy to filter later.
                </p>
              </div>
            )}

            <Separator />

            <div className="space-y-2">
              <Label>Platforms</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PLATFORM_OPTIONS.map(({ id, label, Icon }) => {
                  const active = platforms.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => togglePlatform(id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                        active
                          ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                          : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                      }`}
                      data-testid={`platform-toggle-${id}`}
                    >
                      <span
                        aria-hidden="true"
                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          active
                            ? 'bg-indigo-600 border-indigo-600 text-white'
                            : 'border-zinc-300 bg-white'
                        }`}
                      >
                        {active && (
                          <svg viewBox="0 0 20 20" className="w-3 h-3" fill="currentColor">
                            <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.4 7.4a1 1 0 0 1-1.4 0L3.3 9.5a1 1 0 1 1 1.4-1.4l3.9 3.9 6.7-6.7a1 1 0 0 1 1.4 0Z" clipRule="evenodd" />
                          </svg>
                        )}
                      </span>
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="start">Start date</Label>
                <Input
                  id="start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="campaign-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end">End date</Label>
                <Input
                  id="end"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="campaign-end-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Posts per day per platform</Label>
                <Badge variant="outline" className="font-mono">{postsPerDay}</Badge>
              </div>
              <Slider
                min={1}
                max={5}
                step={1}
                value={[postsPerDay]}
                onValueChange={(v) => setPostsPerDay(v[0])}
                data-testid="campaign-pp-day-slider"
              />
              <p className="text-xs text-zinc-500">Cap is 5/day per platform.</p>
            </div>

            <CaptionLengthPicker
              value={captionLength}
              onChange={setCaptionLength}
            />

            <StyleProfileSelector
              clientId={clientId !== 'none' ? clientId : null}
              currentStyleExample={styleExample}
              currentExtraInstructions={extraInstructions}
              currentCaptionLength={captionLength}
              onApply={({ styleExample: se, extraInstructions: ei, captionLength: cl }) => {
                setStyleExample(se);
                setExtraInstructions(ei);
                setCaptionLength(cl);
                if (se || ei) setAdvancedRevealKey((k) => k + 1);
              }}
            />

            <AdvancedPromptDetails
              key={`adv-${advancedRevealKey}`}
              styleExample={styleExample}
              onStyleExampleChange={setStyleExample}
              extraInstructions={extraInstructions}
              onExtraInstructionsChange={setExtraInstructions}
              defaultOpen={advancedRevealKey > 0}
            />

            <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-3" data-testid="strategy-panel">
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-sm font-medium"><Sparkles className="w-3.5 h-3.5 text-indigo-600" /> Campaign goal (AI strategist)</Label>
                <Select value={goal} onValueChange={(v) => { setGoal(v); setBlueprint(null); }}>
                  <SelectTrigger data-testid="campaign-goal-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No strategy — just generate posts</SelectItem>
                    {GOALS.map((g) => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}
                  </SelectContent>
                </Select>
                {goal !== 'none' && (
                  <p className="text-xs text-zinc-500">The AI infers pillars, content mix, funnel arc & CTAs — review before generating.</p>
                )}
              </div>

              {goal !== 'none' && !blueprint && (
                <Button onClick={buildStrategy} disabled={buildingStrategy} variant="outline" className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-100" data-testid="build-strategy-btn">
                  {buildingStrategy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Build campaign strategy
                </Button>
              )}

              {blueprint && (
                <div className="space-y-2.5 rounded-md bg-white border border-indigo-100 p-3" data-testid="blueprint-card">
                  <div className="space-y-1">
                    <Label className="text-xs text-zinc-500">Positioning</Label>
                    <Textarea value={blueprint.positioning || ''} onChange={(e) => updateBp('positioning', e.target.value)} rows={2} className="text-sm" data-testid="bp-positioning" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-zinc-500">Content pillars (comma-separated)</Label>
                    <Input value={(blueprint.pillars || []).join(', ')} onChange={(e) => updateBp('pillars', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} className="text-sm" data-testid="bp-pillars" />
                  </div>
                  {Array.isArray(blueprint.weekly_themes) && (
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-500">Weekly themes (comma-separated)</Label>
                      <Input value={(blueprint.weekly_themes || []).join(', ')} onChange={(e) => updateBp('weekly_themes', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} className="text-sm" data-testid="bp-themes" />
                    </div>
                  )}
                  {blueprint.content_mix && (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(blueprint.content_mix).map(([k, v]) => (
                        <span key={k} className="text-[11px] bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5">{k.replace('_', ' ')} {v}%</span>
                      ))}
                    </div>
                  )}
                  {Array.isArray(blueprint.funnel_arc) && blueprint.funnel_arc.length > 0 && (
                    <p className="text-xs text-zinc-500">Funnel arc: <span className="text-zinc-700">{blueprint.funnel_arc.join(' → ')}</span></p>
                  )}
                  <button onClick={buildStrategy} disabled={buildingStrategy} className="text-xs text-indigo-600 hover:underline" data-testid="rebuild-strategy-btn">
                    {buildingStrategy ? 'Rebuilding…' : '↻ Regenerate strategy'}
                  </button>

                  <div className="mt-2 pt-3 border-t border-indigo-100 space-y-2" data-testid="email-extension-block">
                    <Label className="text-xs text-zinc-500">Cross-channel: also draft matching emails</Label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Select value={autoEnrollList} onValueChange={setAutoEnrollList}>
                        <SelectTrigger className="text-sm" data-testid="email-autoenroll-select"><SelectValue placeholder="Auto-enroll list (optional)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No auto-enroll</SelectItem>
                          {emailLists.map((l) => <SelectItem key={l.id} value={l.id}>Auto-enroll: {l.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button onClick={draftEmails} disabled={draftingEmails} variant="outline" className="border-pink-300 text-pink-700 hover:bg-pink-50 whitespace-nowrap" data-testid="draft-emails-btn">
                        {draftingEmails ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                        Draft matching emails
                      </Button>
                    </div>
                    {emailExt && (
                      <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2" data-testid="email-extension-result">
                        ✓ Created newsletter “{emailExt.newsletter_subject}” + {emailExt.sequence_steps}-step sequence “{emailExt.sequence_name}”.
                        {emailExt.auto_enroll_list_id ? ' New list members will auto-enroll.' : ''}
                        {' '}<a href="/email/campaigns" className="underline">Open in Email Marketing →</a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <FunnelCtaPicker
              value={ctaFunnel?.id || null}
              onChange={setCtaFunnel}
              refSource="campaign"
              label="Add a lead-capture CTA (optional)"
            />

            {/* Anti-spam scheduling controls */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Spacing between posts</Label>
                <Select
                  value={String(spacingHours)}
                  onValueChange={(v) => setSpacingHours(parseInt(v, 10))}
                >
                  <SelectTrigger data-testid="campaign-spacing-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Every 1 hour</SelectItem>
                    <SelectItem value="2">Every 2 hours</SelectItem>
                    <SelectItem value="3">Every 3 hours</SelectItem>
                    <SelectItem value="4">Every 4 hours (recommended)</SelectItem>
                    <SelectItem value="6">Every 6 hours</SelectItem>
                    <SelectItem value="8">Every 8 hours</SelectItem>
                    <SelectItem value="12">Every 12 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Earliest post each day (UTC)</Label>
                <Select
                  value={String(dailyStartHour)}
                  onValueChange={(v) => setDailyStartHour(parseInt(v, 10))}
                >
                  <SelectTrigger data-testid="campaign-start-hour-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">12 AM</SelectItem>
                    <SelectItem value="6">6 AM (recommended)</SelectItem>
                    <SelectItem value="8">8 AM</SelectItem>
                    <SelectItem value="9">9 AM</SelectItem>
                    <SelectItem value="12">12 PM</SelectItem>
                    <SelectItem value="18">6 PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-md bg-indigo-50 border border-indigo-100 px-3 py-2 text-xs text-indigo-700 flex items-start gap-2">
              <CalendarDays className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {totalEstimate > 0
                  ? `Will draft ~${totalEstimate} posts across ${platforms.length || 0} platform(s) over ${dayCount} day(s).`
                  : 'Pick platforms + dates to see the plan size.'}
              </span>
            </div>

            {previewMeta && previewMeta.spilled_days > 0 && (
              <div
                className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2"
                data-testid="campaign-spillover-notice"
              >
                <CalendarDays className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  At {spacingHours}h spacing this campaign extends{' '}
                  <strong>{previewMeta.spilled_days} day(s)</strong> past your end date.
                  Last post:{' '}
                  {new Date(previewMeta.last_scheduled_for).toLocaleDateString(undefined, {
                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                  })}.
                </span>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handlePreview}
                disabled={previewing || saving || (goal !== 'none' && !blueprint)}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="campaign-preview-btn"
                title={goal !== 'none' && !blueprint ? 'Build the campaign strategy first' : undefined}
              >
                {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {goal !== 'none' ? 'Generate strategic campaign' : 'Generate plan'}
              </Button>
              {previewItems.length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleSaveAsDrafts}
                  disabled={saving}
                  data-testid="campaign-save-drafts-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save as drafts
                </Button>
              )}
              {previewItems.length > 0 && (
                <Button
                  onClick={() => setLaunchOpen(true)}
                  disabled={saving || previewing}
                  className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white"
                  data-testid="launch-full-campaign-btn"
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  Launch full campaign
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* RIGHT — Preview */}
        <Card className="border-zinc-200">
          <CardHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-indigo-600" />
                  Preview ({previewItems.length})
                </CardTitle>
                <CardDescription>
                  Review the AI plan. Saving creates drafts in <strong>My Posts</strong> — nothing publishes automatically.
                </CardDescription>
              </div>
              {previewItems.length > 8 && (
                <div
                  className="inline-flex rounded-md border border-zinc-200 p-0.5 bg-white"
                  role="tablist"
                  aria-label="Preview view mode"
                  data-testid="campaign-view-mode-toggle"
                >
                  <button
                    type="button"
                    onClick={() => setViewMode('detailed')}
                    className={`px-2.5 py-1 text-[11px] font-medium rounded flex items-center gap-1 transition-colors ${
                      viewMode === 'detailed'
                        ? 'bg-indigo-600 text-white'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                    data-testid="campaign-view-mode-detailed"
                  >
                    <Layers className="w-3 h-3" />
                    Detailed
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('scan')}
                    className={`px-2.5 py-1 text-[11px] font-medium rounded flex items-center gap-1 transition-colors ${
                      viewMode === 'scan'
                        ? 'bg-indigo-600 text-white'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                    data-testid="campaign-view-mode-scan"
                  >
                    <ScanText className="w-3 h-3" />
                    Quick scan
                  </button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[640px] overflow-y-auto pr-1">
            {previewItems.length === 0 && !previewing && (
              <div className="rounded-md border border-dashed border-zinc-200 px-4 py-12 text-center text-sm text-zinc-500" data-testid="campaign-preview-empty">
                <AlertCircle className="w-6 h-6 text-zinc-300 mx-auto mb-2" />
                Fill in the brief, then click <strong>Generate plan</strong>.
              </div>
            )}
            {previewing && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                <span className="ml-2 text-sm text-zinc-500">AI is drafting your campaign…</span>
              </div>
            )}
            {previewItems.length > 0 && viewMode === 'scan' && (
              <Accordion
                type="multiple"
                defaultValue={previewWeeks.slice(0, 1).map((w) => w.key)}
                className="space-y-2"
              >
                {previewWeeks.map((week) => (
                  <AccordionItem
                    key={week.key}
                    value={week.key}
                    className="border border-zinc-200 rounded-md px-3 [&[data-state=open]]:border-indigo-300 bg-white"
                    data-testid={`scan-week-${week.key}`}
                  >
                    <AccordionTrigger className="text-sm font-semibold text-zinc-900 hover:no-underline py-2.5">
                      <span className="flex items-center gap-2">
                        Week of {week.label}
                        <span className="text-[11px] text-zinc-500 font-normal">
                          {week.count} post{week.count === 1 ? '' : 's'}
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pt-1 pb-3">
                      <ul className="divide-y divide-zinc-100">
                        {week.items.map((it, idx) => (
                          <li
                            key={`${week.key}-${idx}`}
                            className="py-2 flex gap-3 items-start text-xs"
                            data-testid={`scan-item-${week.key}-${idx}`}
                          >
                            <span className="text-[10px] uppercase font-semibold tracking-wide text-indigo-600 w-16 flex-shrink-0 pt-0.5">
                              {it.platform.replace('_', ' ')}
                            </span>
                            <span className="text-[10px] text-zinc-500 w-20 flex-shrink-0 pt-0.5">
                              {new Date(it.scheduled_for).toLocaleString('en-US', {
                                weekday: 'short', hour: 'numeric', minute: '2-digit',
                              })}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-zinc-900 truncate">{it.post_title}</div>
                              <div className="text-zinc-600 line-clamp-1">
                                {(it.caption || '').replace(/\n/g, ' ').slice(0, 140)}
                              </div>
                              {it.image_prompt && (
                                <div className="text-zinc-400 italic line-clamp-1 mt-0.5">
                                  🖼 {it.image_prompt}
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
            {viewMode === 'detailed' && previewItems.map((it, i) => (
              <div
                key={i}
                className="rounded-md border border-zinc-200 p-3 space-y-2 hover:border-indigo-200 transition-colors"
                data-testid={`campaign-preview-item-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 capitalize">
                    {it.platform.replace('_', ' ')}
                  </Badge>
                  <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                    <CalendarDays className="w-3 h-3" />
                    {new Date(it.scheduled_for).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </span>
                </div>
                <h4 className="text-sm font-semibold text-zinc-900 leading-tight">{it.post_title}</h4>
                <p className="text-xs text-zinc-700 whitespace-pre-wrap line-clamp-4">{it.caption}</p>
                <p className="text-[11px] text-indigo-600 font-mono break-words">{it.hashtags}</p>
                {it.image_prompt && (
                  <p className="text-[11px] text-zinc-500 italic line-clamp-2">
                    🖼 {it.image_prompt}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {previewItems.length > 0 && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-emerald-800">
            <strong>{previewItems.length}</strong> drafts ready. Save them to <strong>My Posts</strong>, or launch the whole funnel (schedule + images + matching emails) in one click.
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSaveAsDrafts}
              disabled={saving}
              variant="outline"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-100"
              data-testid="campaign-save-drafts-bottom-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Save all as drafts
            </Button>
            <Button
              onClick={() => setLaunchOpen(true)}
              disabled={saving || previewing}
              className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white"
              data-testid="launch-full-campaign-bottom-btn"
            >
              <Rocket className="w-4 h-4 mr-2" />
              Launch full campaign
            </Button>
          </div>
        </div>
      )}

      <LaunchCampaignDialog
        open={launchOpen}
        onOpenChange={setLaunchOpen}
        items={previewItems}
        campaignPayload={buildPayload()}
        blueprint={blueprint}
        goal={goal}
        emailCtaUrl={ctaFunnel ? buildTrackedUrl(ctaFunnel, 'email') : null}
        emailLists={emailLists}
      />
    </div>
  );
};

export default AIPostManagerPage;
